import type { ComponentProps } from "react"
import { tv } from "tailwind-variants"
import { backgroundVariants } from "../../styles/utils/background"
import { insetBorderVariants } from "../../styles/utils/insetBorder"
import { scaleTransformVariants } from "../../styles/utils/scaleTransform"
import { classNames } from "../../utils/classNames"
import { Block } from "../Block"
import { Button, type ButtonProps } from "../Button"
import { Flex, type FlexProps } from "../Flex"
import { FlexCenter } from "../FlexCenter"
import { FlexColumn, type FlexColumnProps } from "../FlexColumn"
import { SpaceBetween, type SpaceBetweenProps } from "../SpaceBetween"
import { TextBody } from "../Text"

export type CardProps = FlexColumnProps & {
  active?: boolean
  hoverable?: boolean
}

const cardVariants = tv({
  base: classNames(
    "group relative z-[1] h-full cursor-pointer overflow-hidden rounded-lg shadow-xs",
    insetBorderVariants({ positioning: "absolute" }),
    backgroundVariants({ background: "primary" }),
  ),
  variants: {
    active: {
      true: "outline outline-2 outline-blue-3",
      false: classNames("inactive-group"),
    },
    hoverable: {
      true: "",
      false: "",
    },
  },
  defaultVariants: {
    hoverable: true,
  },
})

export function Card({
  className,
  active = false,
  hoverable = true,
  children,
  ...rest
}: CardProps) {
  return (
    <FlexColumn
      className={classNames(cardVariants({ active, hoverable }), className)}
      {...rest}
    >
      <div
        className={classNames(
          hoverable ? scaleTransformVariants() : "",
          "flex h-full w-full flex-col overflow-hidden rounded-inherit",
        )}
      >
        {children}
      </div>
    </FlexColumn>
  )
}

export function CardContent({ className, ...rest }: ComponentProps<"article">) {
  return (
    <article
      className={classNames("relative grow overflow-hidden", className)}
      {...rest}
    />
  )
}

export function CardContentMediaContent({
  className,
  ...rest
}: ComponentProps<typeof Block>) {
  return (
    <Block
      className={classNames("flex h-full overflow-hidden", className)}
      {...rest}
    />
  )
}

export function CardContentBadge({
  className,
  active,
  ...rest
}: ComponentProps<typeof FlexCenter> & {
  active?: boolean
}) {
  return (
    <Block
      className={classNames(
        "absolute top-2 right-2 transition-opacity duration-150",
        !active &&
          "lg:group-[.inactive-group:hover]:opacity-100 lg:group-[.inactive-group]:opacity-0",
        className,
      )}
      {...rest}
    />
  )
}

export const CardContentMedia = CardContentMediaContent

export function CardFooter({ className, ...rest }: FlexColumnProps) {
  return <FlexColumn className={classNames("relative", className)} {...rest} />
}

export function CardFooterSection({ className, ...rest }: SpaceBetweenProps) {
  return (
    <SpaceBetween
      className={classNames("h-12 w-full px-3 first:items-center", className)}
      {...rest}
    />
  )
}

export const CardFooterDescription = TextBody

export function CardFooterTitle(props: ComponentProps<typeof TextBody>) {
  return <TextBody weight="semibold" {...props} />
}

type CardActionContentProps = {
  floating?: boolean
} & FlexProps

export function CardActions({
  className,
  floating,
  ...rest
}: CardActionContentProps) {
  return (
    <Flex
      className={classNames(
        "-bottom-px absolute inset-x-0 h-12",
        "transition duration-150 ease-out-circ group-hover:duration-150 group-hover:ease-out-circ",
        "group-hover:slide-in-from-bottom group-hover:translate-y-0 group-hover:animate-in",
        "focus-within:slide-in-from-bottom focus-within:translate-y-0 focus-within:animate-in",
        {
          "m-2 translate-y-[calc(100%+theme(spacing.2))] overflow-hidden rounded":
            floating,
          "translate-y-full": !floating,
        },
        className,
      )}
      {...rest}
    />
  )
}

export function CardActionButton({ className, ...rest }: ButtonProps) {
  return (
    <Button
      className={classNames(
        "size-full justify-start rounded-none bg-blue-3 px-0 hover:bg-blue-2 active:bg-blue-2",
        className,
      )}
      {...rest}
    />
  )
}

export function CardFooterBadge({
  className,
  ...rest
}: ComponentProps<typeof FlexCenter>) {
  return (
    <FlexCenter
      className={classNames(
        "h-5 gap-1 rounded bg-bg-additional-1 px-1 text-text-secondary transition-colors",
        className,
      )}
      {...rest}
    />
  )
}

export const CardActionSection = CardFooterSection
